import { useState } from 'react';
import { Box, Button, Typography, useTheme, useMediaQuery, IconButton } from '@mui/material';
import { useRouter } from 'next/router';
import { getPaymentOptions } from 'src/api/paymentApi';
import { debounceSmall } from 'src/helpers/debounceFuncs';
import PaymentsIcon from '@mui/icons-material/PaymentsOutlined';
import HandshakeIcon from '@mui/icons-material/HandshakeOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ContactSupportIcon from '@mui/icons-material/ContactSupportOutlined';
import { isL2Claim } from 'src/helpers/claimHelper';
import { issueType } from 'src/helpers/issueType';
import { formatAmountToISKCurrencyString } from 'src/helpers/formatAmountToString';
import InfoModal from './InfoModal';

export default function ClaimantActions({
  claimantClaims,
  selectedClaims,
  setOperationError,
  onIssueDrawerOpen,
  setPreSelectedIssueType,
}) {
  const router = useRouter();
  const [isLoading, setIsLoading] = useState(false);
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  function getSelectedClaimantClaims() {
    return claimantClaims.filter((claim) => selectedClaims.includes(claim.claimId));
  }

  function getTotalSelectedAmount() {
    const selectedClaimantClaims = getSelectedClaimantClaims();
    return selectedClaimantClaims.reduce((total, claim) => {
      return total + (claim.totalRemaining || 0);
    }, 0);
  }

  const countClaimCategories = (claimsArray) => {
    const hasLegalCollection = claimsArray.some((claim) => claim.product === 'LegalCollection');
    const hasLegalCollection2 = claimsArray.some((claim) => claim.product === 'LegalCollection2');
    const hasOtherTypes = claimsArray.some(
      (claim) => claim.product !== 'LegalCollection' && claim.product !== 'LegalCollection2'
    );

    return {
      hasLegalCollection,
      hasLegalCollection2,
      hasOtherTypes,
      categoriesCount: [hasLegalCollection, hasLegalCollection2, hasOtherTypes].filter(Boolean).length,
    };
  };

  function onGetPaymentOptionsSuccess(options, isForAgreement) {
    setOperationError(null);
    const pathname = isForAgreement ? '/semja' : '/greida';
    const selectedClaimantClaims = getSelectedClaimantClaims();
    const claimIds = selectedClaimantClaims.map((claim) => claim.claimId);

    debounceSmall(() =>
      router.push(
        {
          pathname,
          query: {
            selected: claimIds,
            maxNumberOfInstallments: options.data.maxNumberOfInstallments,
            paymentMethods: options.data.paymentMethods,
            supplementaryClaimIds: options.data.supplementaryClaimIds,
            isForAgreement,
          },
        },
        pathname
      )
    );
  }

  function getPaymentOptionsForClaimant(isForAgreement) {
    const selectedClaimantClaims = getSelectedClaimantClaims();
    const claimIds = selectedClaimantClaims.map((claim) => claim.claimId);
    getPaymentOptions(
      {
        claimIds,
        isForAgreement,
      },
      setIsLoading,
      (options) => onGetPaymentOptionsSuccess(options, isForAgreement),
      setOperationError
    );
  }

  function getAgreementError() {
    const selectedClaimantClaims = getSelectedClaimantClaims();

    if (!selectedClaimantClaims || selectedClaimantClaims.length === 0) {
      return '';
    }

    if (selectedClaimantClaims.length > 1) {
      const { categoriesCount } = countClaimCategories(selectedClaimantClaims);

      if (categoriesCount > 1) {
        return 'Ekki er hægt að semja um lögfræðiinnheimtu ásamt öðrum málum.';
      }
    }

    if (!selectedClaimantClaims.every((x) => x.isAgreementAllowed)) {
      return `Ekki er hægt að semja um greiðslur fyrir innheimtumál: ${selectedClaimantClaims
        .filter((x) => !x.isAgreementAllowed)
        .map((x) => x.claimId)
        .join(', ')} á vefnum.`;
    }

    if (selectedClaimantClaims.length > 1) {
      const collectionTypes = new Map();

      selectedClaimantClaims.forEach((claim) => {
        if (!collectionTypes.has(claim.product)) {
          collectionTypes.set(claim.product, []);
        }
        collectionTypes.get(claim.product).push(claim.claimId);
      });

      if (collectionTypes.size > 1) {
        const productTypes = Array.from(collectionTypes.keys());
        const isAllowedCombination =
          collectionTypes.size === 2 &&
          productTypes.includes('SecondaryCollection') &&
          productTypes.includes('DebtSurveillance');

        if (!isAllowedCombination) {
          return 'Til að semja þurfa öll valin mál að tilheyra sömu innheimtu.';
        }
      }
    }

    if (
      selectedClaimantClaims.every((x) => x.product === 'DebtSurveillance') ||
      selectedClaimantClaims.every((x) => x.claimantNumber === selectedClaimantClaims[0].claimantNumber)
    ) {
      return '';
    }
    return 'Greiðsludreifing er ekki leyfð. Valin innheimtumál verða öll að tilheyra sama kröfuhafa.';
  }

  function getPaymentError() {
    const selectedClaimantClaims = getSelectedClaimantClaims();

    if (!selectedClaimantClaims || selectedClaimantClaims.length === 0) {
      return '';
    }

    if (selectedClaimantClaims.length > 1) {
      const { categoriesCount } = countClaimCategories(selectedClaimantClaims);

      if (categoriesCount > 1) {
        return 'Ekki er hægt að greiða mál í löginnheimtu með öðrum málum.';
      }
    }

    if (selectedClaimantClaims.filter((x) => x.isPaymentAllowed).length !== selectedClaimantClaims.length) {
      return 'Val inniheldur mál sem ekki er hægt að greiða';
    }
    return '';
  }

  function handleAgreementClick() {
    const selectedClaimantClaims = getSelectedClaimantClaims();
    if (selectedClaimantClaims.length > 0 && isL2Claim(selectedClaimantClaims[0].claimId)) {
      setPreSelectedIssueType(issueType.PayerAgreementRequest);
      onIssueDrawerOpen(true);
    } else {
      getPaymentOptionsForClaimant(true);
    }
  }

  const agreementError = getAgreementError();
  const paymentError = getPaymentError();
  const selectedClaimantClaims = getSelectedClaimantClaims();
  const hasNoSelection = !selectedClaimantClaims || selectedClaimantClaims.length === 0;
  const totalAmount = getTotalSelectedAmount();

  const displayError = paymentError || agreementError;

  return (
    <>
      <Box
        sx={{
          mt: 1,
          mb: 1,
          display: 'flex',
          alignItems: isMobile ? 'stretch' : 'center',
          justifyContent: 'space-between',
          paddingRight: '15px',
          paddingLeft: '15px',
          gap: 2,
          flexDirection: isMobile ? 'column' : 'row',
        }}
      >
        <Box
          sx={{
            flex: isMobile ? 'none' : '1',
            display: 'flex',
            justifyContent: 'flex-start',
            order: isMobile ? 3 : 1,
            minHeight: 7,
          }}
        >
          <Box
            sx={{
              backgroundColor: displayError ? '#FAE0A799' : 'transparent',
              padding: '18px 16px',
              maxWidth: isMobile ? '100%' : 'inherit',
              minWidth: displayError ? '200px' : '0px',
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              visibility: displayError ? 'visible' : 'hidden',
              opacity: displayError ? 1 : 0,
              transition: 'opacity 0.2s ease-in-out',
            }}
          >
            <WarningAmberIcon
              sx={{
                fontSize: '24px',
                flexShrink: 0,
              }}
            />
            <Typography
              variant="body2"
              sx={{
                fontSize: '16px',
                fontWeight: 400,
                margin: 0,
                lineHeight: 1.4,
              }}
            >
              {displayError || 'Placeholder text to maintain height'}
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: isMobile ? 'center' : 'flex-end',
            order: isMobile ? 2 : 2,
            minHeight: '56px',
          }}
        >
          <Typography
            variant="body1"
            sx={{
              fontSize: '16px',
              fontWeight: 450,
              whiteSpace: 'nowrap',
              opacity: hasNoSelection ? 0.5 : 1,
              transition: 'opacity 0.2s ease-in-out',
            }}
          >
            Samtals valið: {formatAmountToISKCurrencyString(totalAmount)}
          </Typography>
        </Box>

        <Box
          sx={{
            display: 'flex',
            gap: 1,
            flexDirection: isMobile ? 'column' : 'row',
            width: isMobile ? '100%' : 'auto',
            order: isMobile ? 1 : 3,
            flexShrink: 0,
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Button
            size="medium"
            variant="contained"
            onClick={() => getPaymentOptionsForClaimant(false)}
            disabled={hasNoSelection || !!paymentError || isLoading}
            startIcon={<PaymentsIcon />}
            sx={{
              backgroundColor: '#325127',
              width: isMobile ? '100%' : 'auto',
              '&:hover': {
                backgroundColor: '#2a4420',
              },
              '&.Mui-disabled': {
                backgroundColor: '#97a19d',
                color: '#ffffff',
              },
            }}
          >
            Greiða
          </Button>

          <Button
            size="medium"
            variant="contained"
            onClick={handleAgreementClick}
            disabled={hasNoSelection || !!agreementError || isLoading}
            startIcon={<HandshakeIcon />}
            sx={{
              backgroundColor: '#325127',
              width: isMobile ? '100%' : 'auto',
              '&:hover': {
                backgroundColor: '#2a4420',
              },
              '&.Mui-disabled': {
                backgroundColor: '#97a19d',
                color: '#ffffff',
              },
            }}
          >
            Semja
          </Button>

          {isMobile ? (
            <Button
              variant="text"
              onClick={() => setIsInfoModalOpen(true)}
              sx={{
                color: '#73AF69',
                textTransform: 'none',
                fontSize: '16px',
                fontWeight: 400,
                width: '100%',
                justifyContent: 'center',
                '&:hover': {
                  backgroundColor: 'rgba(50, 81, 39, 0.1)',
                },
              }}
            >
              Upplýsingar um greiðslusamkomulög
            </Button>
          ) : (
            <IconButton
              onClick={() => setIsInfoModalOpen(true)}
              sx={{
                color: '#325127',
                '&:hover': {
                  backgroundColor: 'rgba(50, 81, 39, 0.1)',
                },
              }}
            >
              <ContactSupportIcon
                sx={{
                  fontSize: '24px',
                }}
              />
            </IconButton>
          )}
        </Box>
      </Box>
      <InfoModal open={isInfoModalOpen} onClose={() => setIsInfoModalOpen(false)} />
    </>
  );
}
